/**
 * Utility functions for hash management
 */

import { hashManager } from './HashManager';
import type { HashChangeOptions } from './types';

/**
 * Clean hash string (remove # prefix if present)
 */
export function cleanHash(hash: string): string {
  return hash.replace(/^#/, '');
}

/**
 * Add hash prefix if not present
 */
export function ensureHashPrefix(hash: string): string {
  return hash.startsWith('#') ? hash : `#${hash}`;
}

/**
 * Extract hash from full URL
 */
export function extractHashFromUrl(url: string): string {
  const hashIndex = url.indexOf('#');
  return hashIndex !== -1 ? url.substring(hashIndex + 1) : '';
}

/**
 * Build URL with hash
 */
export function buildUrlWithHash(pathname: string, hash: string, search?: string): string {
  const cleanedHash = cleanHash(hash);
  const searchPart = search || '';
  return cleanedHash 
    ? `${pathname}${searchPart}#${cleanedHash}`
    : `${pathname}${searchPart}`;
}

/**
 * Check if hash corresponds to an existing element
 */
export function hashElementExists(hash: string): boolean {
  if (typeof window === 'undefined') return false;
  const cleanedHash = cleanHash(hash);
  return !!document.getElementById(cleanedHash);
}

/**
 * Get element by hash ID
 */
export function getElementByHash(hash: string): HTMLElement | null {
  if (typeof window === 'undefined') return null;
  const cleanedHash = cleanHash(hash);
  return document.getElementById(cleanedHash);
}

/**
 * Scroll to hash with default options
 */
export function scrollToHash(hash: string, options?: HashChangeOptions): boolean {
  return hashManager.scrollToElement(hash, options);
}

/**
 * Set hash with default options
 */
export function setHash(hash: string, options?: HashChangeOptions): void {
  hashManager.setHash(hash, options);
}

/**
 * Clear current hash
 */
export function clearHash(): void {
  hashManager.clearHash();
}

/**
 * Get current hash
 */
export function getCurrentHash(): string {
  return hashManager.getCurrentHash();
}

/**
 * Check if current hash matches given hash
 */
export function isCurrentHash(hash: string): boolean {
  return hashManager.isCurrentHash(hash);
}

/**
 * Generate a unique hash ID based on content
 */
export function generateHashId(content: string): string {
  return content
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/--+/g, '-') // Replace multiple hyphens with single
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
}

/**
 * Validate hash format
 */
export function isValidHash(hash: string): boolean {
  if (!hash) return false;
  
  const cleanedHash = cleanHash(hash);
  
  // Check if it's a valid ID (alphanumeric, hyphens, underscores)
  return /^[a-zA-Z0-9_-]+$/.test(cleanedHash);
}

/**
 * Check if hash is a comment hash (format: comment-{id}-{subId})
 */
export function isCommentHash(hash?: string): boolean {
  const targetHash = hash || getCurrentHash();
  if (!targetHash) return false;
  
  const cleanedHash = cleanHash(targetHash);
  
  // Check if it matches the comment pattern: comment-{numbers}-{numbers}
  return /^comment-\d+-\d+$/.test(cleanedHash);
}

/**
 * Extract comment IDs from comment hash
 */
export function parseCommentHash(hash?: string): { commentId: string; subId: string } | null {
  const targetHash = hash || getCurrentHash();
  if (!isCommentHash(targetHash)) return null;
  
  const cleanedHash = cleanHash(targetHash);
  const match = cleanedHash.match(/^comment-(\d+)-(\d+)$/);
  
  if (match) {
    return {
      commentId: match[1],
      subId: match[2],
    };
  }
  
  return null;
}

/**
 * Get hash from current URL or provided URL
 */
export function getHashFromUrl(url?: string): string {
  if (typeof window === 'undefined') return '';
  
  const targetUrl = url || window.location.href;
  return extractHashFromUrl(targetUrl);
}

/**
 * Create hash change event handler
 */
export function createHashHandler(
  callback: (hash: string, element?: HTMLElement | null) => void
): () => void {
  return hashManager.onHashChange(callback);
}

/**
 * Batch hash operations (useful for multiple hash-related updates)
 */
export function batchHashOperations(operations: (() => void)[]): void {
  // Temporarily disable hash change notifications
  const originalHandlers = (hashManager as unknown as { handlers: Set<unknown> }).handlers;
  (hashManager as unknown as { handlers: Set<unknown> }).handlers = new Set();
  
  try {
    operations.forEach(operation => operation());
  } finally {
    // Restore handlers and notify once
    (hashManager as unknown as { handlers: Set<unknown>; notifyHandlers: (hash: string) => void }).handlers = originalHandlers;
    const currentHash = hashManager.getCurrentHash();
    (hashManager as unknown as { notifyHandlers: (hash: string) => void }).notifyHandlers(currentHash);
  }
}
